// src/components/SubscribeNow.js
//
// "Subscribe now" — lets someone pay during a trial instead of waiting for it
// to run out.
//
// ===========================================================================
// WHY THIS NO LONGER SENDS PEOPLE STRAIGHT TO A PAYMENT LINK
// ===========================================================================
// A buy.stripe.com Payment Link ends on Stripe's own confirmation page:
//
//     Thanks for subscribing
//     After your trial, a payment to SMART BOARD LEARNING will appear on
//     your statement.
//
// and that page has no way back. There is no button, and the site that sent
// them there is not mentioned. Stripe does support a redirect after payment,
// but it is configured per link in the Stripe Dashboard — it CANNOT be passed
// as a URL parameter, so no amount of client-side code fixes a Payment Link
// that was created without one. Someone who just paid is left on a dead end
// wondering whether it worked.
//
// A Checkout Session created by the `createCheckoutSession` callable takes
// `success_url` and `cancel_url` as arguments, which is what /choose-plan
// already does. Same Stripe checkout, same price, but the customer lands back
// on /upgrade-success, which waits for the webhook and then opens their
// dashboard.
//
// The Payment Links are kept as a fallback. An earlier version of this file
// used them *because* the callable was unreachable from the browser (a
// rejected CORS preflight), and if that comes back the button must still
// take money rather than throw. `client_reference_id` is appended so the
// webhook can still match the payment to an account on that path.
//
// If you find the fallback firing in production, fix the callable rather than
// settling for it — see README-fixes.md, "The dead end after payment".

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebaseConfig";
import { PRODUCT_CATALOG } from "../data/signup/product-catalog";
import { gradeToken, SIGNUP_ROLES, resolvePlanKey, PLANS } from "../config/plans";
import useAccessLevel from "../hooks/useAccessLevel";

/**
 * Which plan family this person buys. Their granted role is "unpaid" until
 * they pay, so this reads `intendedRole` — what they said they were at
 * signup — and falls back to educator.
 */
function billingRoleFor(profile = {}) {
  const intended = String(profile.intendedRole || "").toLowerCase();
  if (intended === SIGNUP_ROLES.STUDENT) return SIGNUP_ROLES.STUDENT;
  if (intended === SIGNUP_ROLES.PARENT) return SIGNUP_ROLES.PARENT;
  return SIGNUP_ROLES.EDUCATOR;
}

/**
 * The PRODUCT_CATALOG key, used for the price labels and for the fallback
 * Payment Link. A parent buys the student plan in that catalogue.
 */
function catalogKeyFor(profile = {}) {
  const state = String(profile.state || "").toLowerCase();
  const grade = gradeToken(profile.grade);
  if (!state || !grade) return null;

  const role = billingRoleFor(profile);
  const catalogRole =
    role === SIGNUP_ROLES.PARENT ? SIGNUP_ROLES.STUDENT : role;

  return `${state}-${grade}-${catalogRole}`;
}

/**
 * Fallback only. Payment Link with the fields Stripe forwards to the session.
 *
 * `client_reference_id` is the one that matters — without it the webhook has
 * a payment and no idea whose it is.
 */
export function paymentLinkFor({ profile, uid, billing = "monthly" }) {
  const key = catalogKeyFor(profile);
  if (!key) return null;

  const product = PRODUCT_CATALOG[key];
  if (!product) return null;

  const base =
    billing === "yearly" ? product.stripeYearly : product.stripeMonthly;

  // A plan with no link yet renders nothing rather than a dead button.
  if (!base) return null;

  const url = new URL(base);
  if (uid) url.searchParams.set("client_reference_id", uid);
  if (profile.email) url.searchParams.set("prefilled_email", profile.email);

  return { url: url.toString(), product, key };
}

const SubscribeNow = ({ profile: profileProp, uid, compact = false }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const {
    hasPaidSubscription,
    loading: accessLoading,
    profile: liveProfile,
    user,
  } = useAccessLevel();

  const profile = liveProfile || profileProp || {};
  const accountId = uid || user?.uid;

  const monthly = paymentLinkFor({ profile, uid: accountId, billing: "monthly" });
  const yearly = paymentLinkFor({ profile, uid: accountId, billing: "yearly" });

  // Nothing to sell to someone who already bought. This is a second line of
  // defence: every call site already gates on hasPaidSubscription, but a page
  // holding a stale profile object would otherwise keep offering the trial
  // upsell to a paying customer.
  if (accessLoading || hasPaidSubscription) return null;

  /**
   * Checkout Session first — it is the only path that comes back to the site.
   */
  const startCheckout = async (billing) => {
    setBusy(billing);
    setError("");

    const planKey = resolvePlanKey({ role: billingRoleFor(profile), billing });
    const fallback = billing === "yearly" ? yearly : monthly;

    if (!planKey) {
      if (fallback) {
        window.location.href = fallback.url;
        return;
      }
      navigate("/choose-plan");
      return;
    }

    try {
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession"
      );
      const { data } = await createCheckoutSession({
        planKey,
        quantity: 1,
        state: profile.state,
        grade: profile.grade,
        successUrl: `${window.location.origin}/upgrade-success`,
        // Back to the page they were on, not to a generic plans page.
        cancelUrl: `${window.location.origin}${window.location.pathname}`,
      });

      if (!data?.url) throw new Error("No checkout URL returned.");
      window.location.href = data.url;
    } catch (err) {
      console.error("createCheckoutSession failed:", err?.code, err?.message);

      // The callable is unreachable or erroring. Take the money via the
      // Payment Link rather than showing a dead button — but the customer
      // will land on Stripe's own confirmation page unless that link has a
      // redirect configured in the Stripe Dashboard.
      if (fallback) {
        console.warn(
          "Falling back to a Payment Link. Configure this link's " +
            "'After payment' redirect in the Stripe Dashboard, or the " +
            "customer has no route back to the site."
        );
        window.location.href = fallback.url;
        return;
      }

      setError(
        "We couldn't start checkout just now. Please try again, or open the plans page."
      );
      setBusy(null);
    }
  };

  if (!monthly && !yearly) {
    // No plan configured for this state/grade — send them to the page that
    // can explain the options rather than showing a broken button.
    return (
      <button
        type="button"
        className="sbl-btn"
        onClick={() => navigate("/choose-plan")}
      >
        See plans
      </button>
    );
  }

  const product = (monthly || yearly).product;

  // The price on the button must be the price that gets charged.
  //
  // PRODUCT_CATALOG carries a per-state price ("$9.99/mo" for DC), but
  // createCheckoutSession resolves a flat national plan from config/plans.js.
  // Labelling the button from the catalogue while charging the plan price
  // would show someone $9.99 and bill them $15. So the plan's own label wins
  // whenever the Checkout Session path is available, and the catalogue label
  // is only used for the Payment Link fallback, which really does charge it.
  const priceLabel = (billing) => {
    const planKey = resolvePlanKey({ role: billingRoleFor(profile), billing });
    if (planKey && PLANS[planKey]) return PLANS[planKey].priceLabel;
    return billing === "yearly" ? product.priceYearly : product.priceMonthly;
  };

  if (compact) {
    return (
      <button
        type="button"
        className="sbl-btn"
        disabled={Boolean(busy)}
        onClick={() => startCheckout(monthly ? "monthly" : "yearly")}
      >
        {busy ? "Opening checkout…" : "Subscribe now"}
      </button>
    );
  }

  return (
    <div className="sbl-subscribe">
      <div className="sbl-subscribe-copy">
        <strong>Want to skip the trial?</strong>
        <span>
          Subscribe now and your account switches to paid straight away — the
          rest of your trial isn't lost, it just stops being the thing standing
          between you and full access.
        </span>
        {error && (
          <span style={{ color: "#991b1b", fontWeight: 600 }}>{error}</span>
        )}
      </div>

      <div className="sbl-subscribe-actions">
        {monthly && (
          <button
            type="button"
            className="sbl-btn"
            disabled={Boolean(busy)}
            onClick={() => startCheckout("monthly")}
          >
            {busy === "monthly"
              ? "Opening checkout…"
              : `Monthly · ${priceLabel("monthly")}`}
          </button>
        )}

        {yearly && (
          <button
            type="button"
            className="sbl-btn sbl-btn-quiet"
            disabled={Boolean(busy)}
            onClick={() => startCheckout("yearly")}
          >
            {busy === "yearly"
              ? "Opening checkout…"
              : `Yearly · ${priceLabel("yearly")}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscribeNow;
